<template>
  <section class="section lucky-numbers">
    <h2 class="section-title medium" :style="{ textAlign: titleAlign || 'center' }">{{ title }}</h2>
    
    <div v-if="description" class="section-description">
      <p>{{ description }}</p>
    </div>

    <LotteryFilters
      v-if="!dateFilterOnly"
      :active-country-slugs="activeCountrySlugs"
      @search="handleSearch"
      @filterCountry="handleCountryFilter"
      @filterDate="handleDateFilter"
      @sort="handleSort"
      :show-date-picker="showDatePicker"
    />

    <div v-else class="date-filter-section">
      <LotteryFilters
        :active-country-slugs="activeCountrySlugs"
        @search="handleSearch"
        @filterCountry="handleCountryFilter"
      />
      
      <NextDrawsFilter
        v-model="selectedDate"
        @update:modelValue="handleDateFilter"
        :show-date-picker="showDatePicker"
      />
    </div>

    <div class="numbers-grid">
      
      <LotteryCard
        v-for="game in filteredGames"
        :key="game.id"
        :lottery="game"
        layout="horizontal"
        @bet="handleBet(game)"
        @favorite="handleFavorite(game)"
        @remove="handleRemove(game)"
        :isFavorited="isFavorited"
      />
    </div>

    <div v-if="hasMoreGames" class="load-more-container">
      <button class="load-more-button" @click="loadMore">
        {{ $t('forms.loadMoreNumberGames') }}
      </button>
    </div>
  </section>
</template>
<script lang="ts">
export default {
  name: 'LuckyNumbersSection',
};
</script>
<script setup lang="ts">
import { ref, computed, nextTick } from "vue";
import { defineProps, defineEmits } from "vue";
import LotteryCard from "./LotteryCard.vue";
import LotteryFilters from "./LotteryFilters.vue";
import NextDrawsFilter from "./NextDrawsFilter.vue";
import { iframeCommunication } from "@/utils/iframeCommunication";

const props = defineProps<{
  title?: string;
  description?: string;
  games: any[];
  showDatePicker?: boolean;
  dateFilterOnly?: boolean;
  isFavorited?: (game: any) => boolean;
  selectedDate?: string; // Add selectedDate prop
  titleAlign?: 'left' | 'center' | 'right';
}>();

const emit = defineEmits<{
  (e: 'bet', game: any): void;
  (e: 'favorite', game: any): void;
  (e: 'remove', game: any): void;
  (e: 'dateChange', date: string): void; // Add dateChange emit
}>();

// Pagination state
const displayLimit = ref(10);

const activeCountrySlugs = computed(() => {
  const slugs = new Set<string>();
  for (const game of props.games || []) {
    if (game?.country) {
      slugs.add(String(game.country).toLowerCase());
    }
  }
  return [...slugs];
});

// Filter states
const searchQuery = ref('');
const selectedCountry = ref('');
const selectedDate = ref(props.selectedDate || '');
const sortBy = ref('default');

const isWithinTimeRange = (date: Date, range: string) => {
  const now = new Date();
  const drawDate = getValidDate(date);
  
  // If date is invalid, return false
  if (drawDate.getTime() === 0) return false;
  
  const hours = (drawDate.getTime() - now.getTime()) / (1000 * 60 * 60);
  
  switch (range) {
    case '1h': return hours <= 1;
    case '6h': return hours <= 6;
    case '12h': return hours <= 12;
    case '24h': return hours <= 24;
    default: return true;
  }
};

function sortGames(games: any[], sort: string) {
  switch (sort) {
    case 'time-asc':
      return [...games].sort((a, b) => {
        const dateA = getValidDate(a.cutoff || a.draw_date);
        const dateB = getValidDate(b.cutoff || b.draw_date);
        return dateA.getTime() - dateB.getTime();
      });
    case 'time-desc':
      return [...games].sort((a, b) => {
        const dateA = getValidDate(a.cutoff || a.draw_date);
        const dateB = getValidDate(b.cutoff || b.draw_date);
        return dateB.getTime() - dateA.getTime();
      });
    case 'country-asc':
      return [...games].sort((a, b) => (a.country || '').localeCompare(b.country || '', undefined, { sensitivity: 'base' }));
    case 'country-desc':
      return [...games].sort((a, b) => (b.country || '').localeCompare(a.country || '', undefined, { sensitivity: 'base' }));
    case 'name-asc':
      return [...games].sort((a, b) => (a.game_name || a.name || '').localeCompare(b.game_name || b.name || '', undefined, { sensitivity: 'base' }));
    case 'name-desc':
      return [...games].sort((a, b) => (b.game_name || b.name || '').localeCompare(a.game_name || a.name || '', undefined, { sensitivity: 'base' }));
    default:
      return games;
  }
}

// Helper function to safely parse dates
function getValidDate(dateString: any): Date {
  if (!dateString) return new Date(0); // Return epoch for null/undefined dates
  
  try {
    const dateStr = String(dateString);
    let date: Date;
    
    if (dateStr.includes('+')) {
      // UTC format like "2025-07-30 23:51:30+00:00"
      const datePart = dateStr.split('+')[0];
      date = new Date(datePart + 'Z');
    } else {
      // Fallback for other formats
      date = new Date(dateStr);
    }
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date format:', dateString);
      return new Date(0); // Return epoch for invalid dates
    }
    
    return date;
  } catch (error) {
    console.warn('Error parsing date:', dateString, error);
    return new Date(0); // Return epoch for error cases
  }
}

const filteredGames = computed(() => {
  let filtered = [...props.games];

  if (searchQuery.value || selectedCountry.value || (selectedDate.value && selectedDate.value !== '')) {
    filtered = filtered.filter(draw => {
      // Apply search filter
      const matchesSearch = !searchQuery.value ||
        (draw.country && draw.country.toLowerCase().includes(searchQuery.value.toLowerCase())) ||
        (draw.game_name && draw.game_name.toLowerCase().includes(searchQuery.value.toLowerCase())) ||
        (draw.name && draw.name.toLowerCase().includes(searchQuery.value.toLowerCase()));
      
      // Apply country filter
      const matchesCountry = !selectedCountry.value || draw.country === selectedCountry.value;
      
      // Apply date filter
      const matchesDate = !selectedDate.value || selectedDate.value === '' || (() => {
        if (!draw.draw_date) return false;
        
        try {
          // Parse the draw date properly (handle UTC format)
          const drawDateString = String(draw.draw_date);
          let drawDate: Date;
          
          if (drawDateString.includes('+')) {
            // UTC format like "2025-07-30 23:51:30+00:00"
            const datePart = drawDateString.split('+')[0];
            drawDate = new Date(datePart + 'Z');
          } else {
            // Fallback for other formats
            drawDate = new Date(drawDateString);
          }
          
          // Check if the date is valid
          if (isNaN(drawDate.getTime())) {
            return false;
          }
          
          // Get the date part in YYYY-MM-DD format
          const drawDateFormatted = drawDate.toISOString().split('T')[0];
          
          return drawDateFormatted === selectedDate.value;
        } catch {
          return false;
        }
      })();
      
      return matchesSearch && matchesCountry && matchesDate;
    });
  }

  filtered = sortGames(filtered, sortBy.value);

  return filtered.slice(0, displayLimit.value);
});

const hasMoreGames = computed(() => {
  let filtered = sortGames(props.games, sortBy.value);

  if (searchQuery.value || selectedCountry.value || (selectedDate.value && selectedDate.value !== '')) {
    filtered = filtered.filter(draw => {
      // Apply search filter
      const matchesSearch = !searchQuery.value ||
        (draw.country && draw.country.toLowerCase().includes(searchQuery.value.toLowerCase())) ||
        (draw.game_name && draw.game_name.toLowerCase().includes(searchQuery.value.toLowerCase())) ||
        (draw.name && draw.name.toLowerCase().includes(searchQuery.value.toLowerCase()));
      
      // Apply country filter
      const matchesCountry = !selectedCountry.value || draw.country === selectedCountry.value;
      
      // Apply date filter
      const matchesDate = !selectedDate.value || selectedDate.value === '' || (() => {
        if (!draw.draw_date) return false;
        
        try {
          // Parse the draw date properly (handle UTC format)
          const drawDateString = String(draw.draw_date);
          let drawDate: Date;
          
          if (drawDateString.includes('+')) {
            // UTC format like "2025-07-30 23:51:30+00:00"
            const datePart = drawDateString.split('+')[0];
            drawDate = new Date(datePart + 'Z');
          } else {
            // Fallback for other formats
            drawDate = new Date(drawDateString);
          }
          
          // Check if the date is valid
          if (isNaN(drawDate.getTime())) {
            return false;
          }
          
          // Get the date part in YYYY-MM-DD format
          const drawDateFormatted = drawDate.toISOString().split('T')[0];
          
          return drawDateFormatted === selectedDate.value;
        } catch {
          return false;
        }
      })();
      
      return matchesSearch && matchesCountry && matchesDate;
    });
  }

  return filtered.length > displayLimit.value;
});

const loadMore = () => {
  displayLimit.value += 10;
  
  // Trigger immediate height update after DOM updates with a longer delay
  nextTick(() => {
    // Add extra delay to ensure all content is rendered
    setTimeout(() => {
      iframeCommunication.sendImmediateHeightUpdate();
    }, 100);
  });
};

const handleSearch = (query: string) => {
  searchQuery.value = query;
};

const handleCountryFilter = (country: string) => {
  selectedCountry.value = country;
};

const handleDateFilter = (date: string) => {
  selectedDate.value = date;
  emit('dateChange', date); // Emit dateChange event
};

const handleSort = (sort: string) => {
  sortBy.value = sort;
};

const handleBet = (game: any) => {
  emit('bet', game);
};

const handleFavorite = (game: any) => {
  emit('favorite', game);
};

const handleRemove = (game: any) => {
  emit('remove', game);
};

</script>

<style scoped>

.section {
  margin-bottom: 48px;
}

/* Reduce bottom margin when in iframe mode to prevent unnecessary spacing */
body.iframe-mode .section {
  margin-bottom: 24px;
}


.section-description {
  text-align: center;
  margin-bottom: 32px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

.date-filter-section {
  display: flex;
  flex-direction: row;
  gap: 16px;
  margin-bottom: 24px;
  align-items: flex-start;
}

/* Mobile: Stack filters vertically */
@media (max-width: 768px) {
  .date-filter-section {
    flex-direction: column;
    gap: 12px;
  }
}

.section-description p {
  color: var(--text-primary);
  font-size: 16px;
  line-height: 1.6;
  white-space: pre-line;
}

.numbers-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  padding: 8px 0;
  width: 100%;
  overflow: hidden;
  align-items: start;
}

.numbers-grid > * {
  min-width: 0;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
}



/* Tablet breakpoint */
@media (max-width: 1024px) {
  .numbers-grid {
    gap: 14px;
  }
  
}

/* Mobile breakpoint */
@media (max-width: 768px) {
  .numbers-grid {
    display: flex;
    flex-direction: column;
    gap: 8px;
    overflow-x: unset;
    margin: 0;
    padding: 0;
  }
  
  .numbers-grid > * {
    min-width: 0;
    width: 100%;
    max-width: 100%;
  }
  
  .section-description {
    margin-bottom: 24px;
  }
  
  .section-description p {
    font-size: 15px;
  }
  
}

/* Small mobile breakpoint */
@media (max-width: 480px) {
  .numbers-grid {
    gap: 6px;
  }
  
  .section-description {
    margin-bottom: 20px;
  }
  
  .section-description p {
    font-size: 14px;
  }
  
}
</style>
